import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "lucide-react";
import Button from "../../../../components/Buttons/Button";
import Loader from "../../../../components/Loaders/Loader";
import { ConfirmAlert } from "../../../../components/Alerts/Alerts";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BUTTON_CANCEL,
  BUTTON_SEND,
  CAMPAIGN_APPROVAL_CONFIRM_ENABLE_DESCRIPTION,
  CAMPAIGN_APPROVAL_CONFIRM_ENABLE_TITLE,
  CAMPAIGN_APPROVAL_APPROVED_DESCRIPTION,
  CAMPAIGN_APPROVAL_PENDING_DESCRIPTION,
  CAMPAIGN_APPROVAL_COPY_LINK_BUTTON,
  CAMPAIGN_APPROVAL_REJECTED_DESCRIPTION,
  CAMPAIGN_APPROVAL_SEND_FOR_REVIEW,
  CAMPAIGN_APPROVAL_SHARED_LINK_LABEL,
  CAMPAIGN_APPROVAL_VIEW_COMMENTS_BUTTON,
  CAMPAIGN_APPROVAL_VIEW_REVIEW_BUTTON,
} from "../../../../i18n/keysTranslations";
import {
  getCampaignApprovalStatusLabel,
  getCampaignApprovalStatusSx,
} from "../../../CampaignApproval/utils";
import {
  hasMultipleApprovalReviewers,
  deriveApprovalStatusFromReviewers,
} from "../../../../components/ContentApproval/approvalReviewersUtils";
import {
  BOX_SHADOW_BUTTON,
  BOX_SHADOW_THEME,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import {
  BORDER_RADIUS,
  BORDER_RADIUS_BUTTON,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
  HEIGHT_BUTTON,
} from "../../../../utils/constants";

const INACTIVE_DOT_COLOR = "#9aa0a6";
const APPROVAL_TOOLTIP_MIN_WIDTH = 300;

const CampaignApprovalControls = ({
  approvalData,
  isEnabling = false,
  disabled = false,
  onEnableApproval,
  onCopyLink,
  onViewComments,
}) => {
  const { t } = useTranslationApp();
  const isEnabled = Boolean(approvalData?.enabled);
  const approvalUrl = approvalData?.url || "";
  const approvalStatus = deriveApprovalStatusFromReviewers({
    reviewers: approvalData?.reviewers,
    fallbackStatus: approvalData?.status,
  });
  const isPending = approvalStatus === "pending";
  const isRejected = approvalStatus === "rejected";
  const isApproved = approvalStatus === "approved";
  const isMultiReviewer = hasMultipleApprovalReviewers(approvalData?.reviewers);

  const anchorRef = useRef(null);
  const tooltipContentRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [anchorWidth, setAnchorWidth] = useState(null);

  const syncAnchorWidth = () => {
    if (anchorRef.current) {
      setAnchorWidth(anchorRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    syncAnchorWidth();

    const frameId = requestAnimationFrame(() => {
      syncAnchorWidth();
      setTooltipOpen(true);
    });

    return () => cancelAnimationFrame(frameId);
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled || !tooltipOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      const target = event.target;
      const isInsideAnchor = anchorRef.current?.contains(target);
      const isInsideTooltip = tooltipContentRef.current?.contains(target);

      if (isInsideAnchor || !isInsideTooltip) {
        setTooltipOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEnabled, tooltipOpen]);

  const handleTooltipOpen = () => {
    syncAnchorWidth();
    setTooltipOpen(true);
  };

  const handleViewComments = () => {
    setTooltipOpen(false);
    onViewComments?.();
  };

  const handleStatusButtonClick = () => {
    if (disabled || isEnabling) {
      return;
    }

    setTooltipOpen(false);
    onViewComments?.();
  };

  const onRequestEnableApproval = async () => {
    if (disabled || isEnabling) {
      return;
    }

    setTooltipOpen(false);
    const result = await ConfirmAlert({
      title: t(CAMPAIGN_APPROVAL_CONFIRM_ENABLE_TITLE),
      text: t(CAMPAIGN_APPROVAL_CONFIRM_ENABLE_DESCRIPTION),
      confirmButtonText: t(BUTTON_SEND),
      cancelButtonText: t(BUTTON_CANCEL),
    });

    if (result.isConfirmed) {
      onEnableApproval();
    }
  };

  const statusSx = isEnabled
    ? getCampaignApprovalStatusSx(approvalStatus)
    : null;

  const renderAnchorContent = () => {
    if (isEnabling) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            minWidth: 72,
          }}
        >
          <Loader hasMarginTop={false} size={18} />
        </Box>
      );
    }

    if (!isEnabled) {
      return (
        <Typography
          variant="body1"
          sx={{
            fontWeight: FONT_WEIGHT_BUTTON,
            fontSize: FONT_SIZE_BUTTON,
            whiteSpace: "nowrap",
          }}
        >
          {t(CAMPAIGN_APPROVAL_SEND_FOR_REVIEW)}
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: statusSx?.color || INACTIVE_DOT_COLOR,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: statusSx?.color || "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {getCampaignApprovalStatusLabel(approvalStatus, t)}
        </Typography>
      </Box>
    );
  };

  const tooltipContent = (
    <Box
      ref={tooltipContentRef}
      sx={{
        p: 2,
        boxSizing: "border-box",
        width: "100%",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {isEnabled && (approvalUrl || isMultiReviewer) ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {approvalStatus && (isPending || isRejected || isApproved) ? (
            <Typography
              variant="caption"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                border: "1px solid",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 700,
                display: "inline-block",
                alignSelf: "flex-start",
                ...getCampaignApprovalStatusSx(approvalStatus),
              }}
            >
              {getCampaignApprovalStatusLabel(approvalStatus, t)}
            </Typography>
          ) : null}

          {isPending ? (
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, wordBreak: "break-word" }}
            >
              {t(CAMPAIGN_APPROVAL_PENDING_DESCRIPTION)}
            </Typography>
          ) : null}

          {isRejected ? (
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, wordBreak: "break-word" }}
            >
              {t(CAMPAIGN_APPROVAL_REJECTED_DESCRIPTION)}
            </Typography>
          ) : null}

          {isApproved ? (
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, wordBreak: "break-word" }}
            >
              {t(CAMPAIGN_APPROVAL_APPROVED_DESCRIPTION)}
            </Typography>
          ) : null}

          {isPending && !isMultiReviewer ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: `${BORDER_RADIUS}px`,
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                bgcolor: "fields.background",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t(CAMPAIGN_APPROVAL_SHARED_LINK_LABEL)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                }}
              >
                {approvalUrl}
              </Typography>
            </Box>
          ) : null}

          {isRejected && !isMultiReviewer ? (
            <Button
              type="button"
              color="secondary"
              onClick={handleViewComments}
              sx={{ width: "100%" }}
            >
              {t(CAMPAIGN_APPROVAL_VIEW_COMMENTS_BUTTON)}
            </Button>
          ) : null}
          {!isApproved && isMultiReviewer ? (
            <Button
              type="button"
              color="secondary"
              onClick={handleViewComments}
              sx={{ width: "100%" }}
            >
              {t(CAMPAIGN_APPROVAL_VIEW_REVIEW_BUTTON)}
            </Button>
          ) : null}
          {!isApproved && !isMultiReviewer ? (
            <Button
              type="button"
              startIcon={<Link size={16} />}
              onClick={onCopyLink}
              sx={{ width: "100%" }}
            >
              {t(CAMPAIGN_APPROVAL_COPY_LINK_BUTTON)}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );

  const anchorBox = (
    <Box
      ref={anchorRef}
      onClick={
        !isEnabled
          ? onRequestEnableApproval
          : isMultiReviewer
            ? handleStatusButtonClick
            : undefined
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.5,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: BORDER_RADIUS_BUTTON,
        boxShadow: BOX_SHADOW_BUTTON,
        height: HEIGHT_BUTTON,
        px: 2,
        py: 1,
        bgcolor: "background.paper",
        cursor: disabled || isEnabling ? "not-allowed" : "pointer",
        opacity: disabled || isEnabling ? 0.6 : 1,
        userSelect: "none",
        width: "fit-content",
        flexShrink: 0,
        "&:hover": {
          bgcolor: disabled || isEnabling ? "background.paper" : "action.hover",
        },
      }}
    >
      {renderAnchorContent()}
    </Box>
  );

  if (!isEnabled) {
    return anchorBox;
  }

  return (
    <Tooltip
      open={tooltipOpen && Boolean(anchorWidth)}
      onOpen={handleTooltipOpen}
      disableFocusListener
      disableTouchListener
      disableInteractive={false}
      leaveDelay={100}
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            p: 0,
            m: 0,
            mt: "0px !important",
            maxWidth: "none",
            position: "relative",
            backgroundColor: "background.paper",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: BORDER_RADIUS,
            boxShadow: BOX_SHADOW_THEME,
            color: "text.primary",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -10,
              left: 0,
              width: "100%",
              height: 10,
            },
            minWidth: APPROVAL_TOOLTIP_MIN_WIDTH,
            ...(anchorWidth && {
              width: Math.max(anchorWidth, APPROVAL_TOOLTIP_MIN_WIDTH),
              maxWidth: Math.max(anchorWidth, APPROVAL_TOOLTIP_MIN_WIDTH),
            }),
          },
        },
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 2],
              },
            },
          ],
        },
      }}
      title={tooltipContent}
    >
      {anchorBox}
    </Tooltip>
  );
};

export default CampaignApprovalControls;
